import React from 'react';
import { useLanguage } from '@/contexts';

// Simple share icon component
const ShareIcon: React.FC = () => (
  <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
  </svg>
);

// Simple share button component
interface ShareButtonProps {
  onClick: () => void;
  isVisible: boolean;
}

const ShareButton: React.FC<ShareButtonProps> = ({ onClick, isVisible }) => {
  const { t } = useLanguage();
  
  if (!isVisible) return null;
  
  return (
    <button
      onClick={onClick}
      aria-label="Share prompt"
      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-brand-light-gray focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
    >
      <ShareIcon />
      {t("share")}
    </button>
  );
};

// Simple URL input component
interface ShareUrlInputProps {
  url: string;
}

const ShareUrlInput: React.FC<ShareUrlInputProps> = ({ url }) => (
  <input
    type="text"
    readOnly
    aria-label="Share URL"
    value={url}
    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
  />
);

// Simple copy button component
interface CopyButtonProps {
  onCopy: () => void;
}

const CopyButton: React.FC<CopyButtonProps> = ({ onCopy }) => {
  const { t } = useLanguage();
  
  return (
    <button
      onClick={onCopy}
      aria-label="Copy link"
      className="w-full px-4 py-2 text-sm font-medium text-white bg-brand-dark-blue rounded-md hover:bg-brand-electric-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue"
    >
            {t("copy")}
    </button>
  );
};

// Simple dropdown container component
interface ShareDropdownProps {
  isOpen: boolean;
  children: React.ReactNode;
}

const ShareDropdown: React.FC<ShareDropdownProps> = ({ isOpen, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg z-10 p-4">
      <div className="flex flex-col space-y-3">
        {children}
      </div>
    </div>
  );
};

// Main composed share component
interface ShareComponentProps {
  promptId: string;
  isNewPrompt: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onCopy: () => void;
}

const ShareComponent: React.FC<ShareComponentProps> = ({
  promptId,
  isNewPrompt,
  isOpen,
  onToggle,
  onCopy
}) => {
  const shareUrl = `${window.location.origin}/prompts/${promptId}`;

  return (
    <div className="relative">
      <ShareButton onClick={onToggle} isVisible={!isNewPrompt} />
      <ShareDropdown isOpen={isOpen}>
        <ShareUrlInput url={shareUrl} />
        <CopyButton onCopy={onCopy} />
      </ShareDropdown>
    </div>
  );
};

export default ShareComponent;

// Export individual components for reuse
export { ShareIcon, ShareButton, ShareUrlInput, CopyButton, ShareDropdown };
